import React, { useState, useEffect, useRef } from 'react'
import { useAuthStore } from '@store'
import { useNavigate } from 'react-router-dom'
import apiClient from '@services/api/client'

const MessageBell = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [conversations, setConversations] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef(null)
  const isMarkingRef = useRef(false) // Bloquer le rechargement pendant le marquage

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  // Charger les conversations
  const loadConversations = async () => {
    if (!user?.client_id && !user?.id) {
      return
    }
    
    // Bloquer si marquage en cours
    if (isMarkingRef.current) {
      console.log('[MessageBell] Rechargement bloqué - marquage en cours')
      return
    }

    try {
      const result = await apiClient.get('/conversations')
      // Normaliser la structure réelle de l'API (/conversations)
      const convs = (result.data || []).map(c => ({
        conversation_id:   c.id,
        client_nom:        [c.other_client?.prenom, c.other_client?.nom].filter(Boolean).join(' ') || 'Utilisateur',
        last_message:      c.last_message?.urlArticle
                             ? (c.last_message?.nomArticle || 'Article partagé')
                             : (c.last_message?.message || 'Nouvelle conversation'),
        last_message_date: c.last_message?.created_at || c.updated_at,
        unread_count:      c.unread_count || 0,
      }))
      setConversations(convs.slice(0, 5)) // Garder les 5 dernières

      // Compter les conversations avec messages non lus
      const unread = convs.filter(c => c.unread_count > 0).length
      setUnreadCount(unread)
    } catch (error) {
      console.error('[MessageBell] Erreur chargement:', error)
    }
  }

  // Charger au montage et toutes les 30 secondes
  useEffect(() => {
    if (!user?.client_id && !user?.id) {
      return
    }
    
    loadConversations()
    
    const interval = setInterval(loadConversations, 10000) // 10 secondes
    
    // Écouter l'événement de rechargement
    const handleReloadMessages = () => {
      loadConversations()
    }
    window.addEventListener('reloadMessages', handleReloadMessages)
    
    return () => {
      clearInterval(interval)
      window.removeEventListener('reloadMessages', handleReloadMessages)
    }
  }, [user?.client_id, user?.id])

  // Marquer une conversation comme lue
  const markAsRead = async (conversationId) => {
    try {
      // Bloquer le rechargement
      isMarkingRef.current = true
      
      await apiClient.post('/conversations/' + conversationId + '/read', {})

      // Mettre à jour localement d'abord
      setConversations(prev =>
        prev.map(c => c.conversation_id === conversationId ? { ...c, unread_count: 0 } : c)
      )

      // Recharger après 1 seconde pour confirmer
      setTimeout(() => {
        isMarkingRef.current = false
        loadConversations()
        // Notifier AdminLayout
        window.dispatchEvent(new CustomEvent('reloadMessages'))
      }, 1000)
    } catch (error) {
      console.error('[MessageBell] Erreur marquage lu:', error)
      // Recharger en cas d'erreur
      isMarkingRef.current = false
      loadConversations()
    }
  }

  // Ouvrir une conversation
  const openConversation = (conversationId) => {
    markAsRead(conversationId)
    navigate(`/admin/messages?conversation=${conversationId}`)
    setShowDropdown(false)
  }

  // Formater la date
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return "À l'instant"
    if (diffMins < 60) return `Il y a ${diffMins} min`
    if (diffHours < 24) return `Il y a ${diffHours}h`
    if (diffDays < 7) return `Il y a ${diffDays}j`
    
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setShowDropdown(!showDropdown)}
        className="p-2.5 bg-gray-50 hover:bg-gray-100 rounded-lg relative transition-all shadow-sm border border-gray-200"
      >
        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-md">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Messages Dropdown Menu */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50 animate-slideUp">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">Messages</h3>
            <p className="text-xs text-gray-500">
              {unreadCount > 0 
                ? `Vous avez ${unreadCount} nouveau${unreadCount > 1 ? 'x' : ''} message${unreadCount > 1 ? 's' : ''}`
                : 'Aucun nouveau message'
              }
            </p>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {conversations.length > 0 ? (
              conversations.map((conv, index) => {
                const initials = conv.client_nom?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'UK'
                const colors = [
                  'from-purple-400 to-pink-400',
                  'from-blue-400 to-cyan-400',
                  'from-orange-400 to-red-400',
                  'from-green-400 to-emerald-400',
                  'from-yellow-400 to-orange-400'
                ]
                const color = colors[index % colors.length]
                
                return (
                  <button
                    key={conv.conversation_id}
                    onClick={() => openConversation(conv.conversation_id)}
                    className={`w-full px-4 py-3 hover:bg-gray-50 transition-all flex items-start gap-3 border-l-4 ${
                      conv.unread_count > 0 ? 'border-blue-500 bg-blue-50/30' : 'border-transparent'
                    }`}
                  >
                    {/* Avatar avec initiales */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br ${color} flex items-center justify-center text-white font-bold text-sm shadow-md`}>
                      {initials}
                    </div>

                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-gray-800 text-sm truncate">
                          {conv.client_nom || 'Utilisateur'}
                        </p>
                        <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                          {formatDate(conv.last_message_date || conv.updated_at)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-2 mb-1">
                        {conv.last_message || 'Nouvelle conversation'}
                      </p>
                      {conv.unread_count > 0 && (
                        <span className="inline-block px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                          {conv.unread_count} nouveau{conv.unread_count > 1 ? 'x' : ''}
                        </span>
                      )}
                    </div>
                  </button>
                )
              })
            ) : (
              <div className="px-4 py-8 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="text-sm">Aucune conversation</p>
              </div>
            )}
          </div>
          <div className="px-4 py-2 border-t border-gray-100">
            <button
              onClick={() => {
                navigate('/admin/messages')
                setShowDropdown(false)
              }}
              className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium py-1"
            >
              Voir tous les messages →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default MessageBell
